import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  DIRECTION,
  DISPLAY_STATUS,
  OBSERVATION_STATUS,
  OBSERVATION_TYPE,
  displayStatusMeta,
} from "../../../utils/constants/observations";
import { UserContext } from "../../../utils/contexts/UserContext";
import { formatMonthShort, formatShortDate, formatWeekdayMonthDay, parseLocalISODate, toLocalISODate } from "../../../utils/helpers/dateDisplay";
import FocusStandardChips, {
  visitFocusStandards,
} from "../../../components/scheduling/FocusStandardChips";
import {
  CONDUCT_BTN,
  CONDUCT_ICON_PATH,
  CONDUCT_SOON_BTN,
  isConductDue,
} from "../../../components/scheduling/conductAction";
import LessonEditorModal from "./LessonEditorModal";

// "08:30:00" → "08:30" (24h, matches PeriodPicker)
const formatPeriodTime = (t) => {
  if (!t) return "";
  const [hStr, mStr] = String(t).split(":");
  if (Number.isNaN(parseInt(hStr, 10))) return "";
  return `${hStr.padStart(2, "0")}:${mStr}`;
};

const EMPTY_DAY = {};

const sameLocalDay = (dateA, isoDateB) => {
  if (!isoDateB) return false;
  const b = new Date(isoDateB);
  return (
    dateA.getFullYear() === b.getFullYear() &&
    dateA.getMonth() === b.getMonth() &&
    dateA.getDate() === b.getDate()
  );
};

// Local-midnight Monday of the week containing `d`.
const mondayOf = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(date.getFullYear(), date.getMonth(), diff);
};

const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

// 28-day (4-week) Mon–Fri window starting at `anchorMonday`.
const calendarDaysFrom = (anchorMonday) => {
  const days = [];
  for (let i = 0; i < 28; i++) {
    const d = addDays(anchorMonday, i);
    if (d.getDay() !== 0 && d.getDay() !== 6) days.push(d);
  }
  return days;
};

const statusBadgeClass = (displayStatus) => displayStatusMeta(displayStatus).badge;

const calendarCellClass = (obs) => {
  if (obs.displayStatus === DISPLAY_STATUS.DECLINED) {
    return "bg-rose-50/60 border-rose-200 text-rose-500";
  }
  if (obs.direction === DIRECTION.OUTGOING) {
    return "bg-indigo-600 border-indigo-500 text-white";
  }
  if (obs.type === OBSERVATION_TYPE.OFFICIAL) {
    return "bg-amber-50 border-amber-300 text-amber-900 border-2";
  }
  if (obs.displayStatus === DISPLAY_STATUS.CONFIRMED) {
    return "bg-emerald-50 border-emerald-400 text-emerald-900 border-2";
  }
  return "bg-white border-slate-200 text-slate-900";
};

// One accent system for every card: a left bar plus a whisper-quiet tint in
// the card's semantic hue — the bar carries the signal, the tint is ambience,
// borders stay slate. `label` is the matching text color for the direction
// micro-label.
const listCardAccent = (obs) => {
  if (
    obs.displayStatus === DISPLAY_STATUS.DECLINED ||
    obs.displayStatus === DISPLAY_STATUS.CANCELLED
  ) {
    return { card: "border-l-rose-400 bg-rose-50/30", label: "text-rose-500" };
  }
  if (obs.direction === DIRECTION.OUTGOING) {
    return { card: "border-l-indigo-400 bg-indigo-50/40", label: "text-indigo-600" };
  }
  if (obs.type === OBSERVATION_TYPE.OFFICIAL) {
    return { card: "border-l-amber-400 bg-amber-50/40", label: "text-amber-700" };
  }
  if (obs.displayStatus === DISPLAY_STATUS.PENDING) {
    return { card: "border-l-amber-400 bg-amber-50/30", label: "text-amber-700" };
  }
  if (obs.displayStatus === DISPLAY_STATUS.CONFIRMED) {
    return { card: "border-l-emerald-400 bg-emerald-50/30", label: "text-emerald-700" };
  }
  return { card: "border-l-slate-300 bg-white", label: "text-slate-400" };
};

// Swatches mirror the actual cell/bar styles (CAL-3).
const LEGEND = [
  { key: "legYourLesson", dot: "bg-slate-50 border border-slate-200" },
  { key: "legYourVisit", dot: "bg-indigo-600" },
  { key: "legOfficial", dot: "bg-amber-50 border-2 border-amber-300" },
  { key: "legConfirmedPeer", dot: "bg-emerald-50 border-2 border-emerald-400" },
  { key: "legPending", dot: "bg-amber-100 border border-amber-200" },
];

const FOCUS_RING =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2";

const ICON_PATHS = {
  check: "M5 13l4 4L19 7",
  x: "M6 18L18 6M6 6l12 12",
  undo: "M9 15L3 9m0 0l6-6M3 9h11a6 6 0 010 12h-1",
  conduct: CONDUCT_ICON_PATH,
};

const ActionIcon = ({ path, className = "w-3.5 h-3.5 shrink-0" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d={path} />
  </svg>
);

// One labeled-action vocabulary everywhere, mirroring the detail modal's
// hierarchy: solid confirm, outline decline, tinted-outline restore.
const ACTION_BTN =
  "flex items-center justify-center gap-1.5 min-w-0 text-[10px] font-black uppercase tracking-wide transition-colors";
const CONFIRM_BTN = "bg-emerald-600 text-white hover:bg-emerald-700";
const DECLINE_BTN =
  "bg-white border border-rose-300 text-rose-600 hover:bg-rose-600 hover:border-rose-600 hover:text-white";
const RESTORE_BTN =
  "bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-600 hover:border-emerald-600 hover:text-white";
// Conduct's two weights (CONDUCT_BTN / CONDUCT_SOON_BTN) live in
// components/scheduling/conductAction; these are the same two on the indigo
// cell an outgoing visit gets in the calendar, where white-on-white would be
// invisible.
const CONDUCT_CELL_BTN = "bg-white text-indigo-700 hover:bg-indigo-50";
const CONDUCT_CELL_SOON_BTN = "bg-white/20 text-white hover:bg-white/30";
const conductCellClass = (obs, due) => {
  if (obs.direction === DIRECTION.OUTGOING) {
    return due ? CONDUCT_CELL_BTN : CONDUCT_CELL_SOON_BTN;
  }
  return due ? CONDUCT_BTN : CONDUCT_SOON_BTN;
};

export default function UpcomingObservationsTab({
  observations,
  timetable = [],
  groups = [],
  rubric = null,
  onTimetableChanged,
  calendarViewMode,
  setCalendarViewMode,
  onSelectObservation,
  onConfirm,
  onDecline,
  onRequestVisit,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext) || {};
  const [weekAnchor, setWeekAnchor] = useState(() => mondayOf(new Date()));
  const [slotChooser, setSlotChooser] = useState(null); // { date, weekday, period }
  const [slotVisits, setSlotVisits] = useState(null); // decorated obs[] sharing one slot
  const [lessonEdit, setLessonEdit] = useState(null); // { entry?, weekday, period }

  useEffect(() => {
    if (!slotChooser && !slotVisits) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setSlotChooser(null);
        setSlotVisits(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [slotChooser, slotVisits]);

  const statusLabel = (s) => t(displayStatusMeta(s).labelKey);
  const periodText = (obs) =>
    obs.period
      ? `${t("upcoming.period", { period: obs.period })}${obs.group?.name ? ` (${obs.group.name})` : ""}`
      : "—";
  const sorted = useMemo(
    () =>
      [...observations].sort((a, b) => {
        const aDate = a.scheduled_date ? new Date(a.scheduled_date).getTime() : 0;
        const bDate = b.scheduled_date ? new Date(b.scheduled_date).getTime() : 0;
        return aDate - bDate;
      }),
    [observations],
  );

  // weekday (0-6) → { [period]: entry }
  const lessonByDay = useMemo(() => {
    const map = {};
    timetable.forEach((entry) => {
      if (!map[entry.weekday]) map[entry.weekday] = {};
      map[entry.weekday][entry.period] = entry;
    });
    return map;
  }, [timetable]);

  // List view: pending incoming peers pin to a "needs your response" queue,
  // everything else buckets by day. Same-slot INCOMING visits collapse into
  // one "N visitors" card within each bucket.
  const { listSections, slotTotals } = useMemo(() => {
    const needsResponse = (o) =>
      o.direction === DIRECTION.INCOMING &&
      o.type === OBSERVATION_TYPE.PEER &&
      o.displayStatus === DISPLAY_STATUS.PENDING;
    // Queue/bucket partitioning can split one lesson slot across sections;
    // slot totals let cards surface the visitors they no longer sit beside.
    const totals = new Map();
    sorted.forEach((obs) => {
      if (obs.direction === DIRECTION.INCOMING && obs.scheduled_date && obs.period != null) {
        const key = `${obs.scheduled_date}|${obs.period}`;
        totals.set(key, (totals.get(key) || 0) + 1);
      }
    });
    const collapseSlots = (obsArr) => {
      const items = [];
      const slotIndex = new Map();
      obsArr.forEach((obs) => {
        const groupable =
          obs.direction === DIRECTION.INCOMING && obs.scheduled_date && obs.period != null;
        const key = groupable ? `${obs.scheduled_date}|${obs.period}` : null;
        if (key && slotIndex.has(key)) {
          items[slotIndex.get(key)].push(obs);
        } else {
          if (key) slotIndex.set(key, items.length);
          items.push([obs]);
        }
      });
      return items;
    };

    const sections = [];
    const queue = collapseSlots(sorted.filter(needsResponse));
    if (queue.length > 0) {
      sections.push({
        key: "__queue",
        queue: true,
        items: queue,
        count: queue.reduce((n, l) => n + l.length, 0),
      });
    }
    // sorted is date-ordered, so same-day items are adjacent. Keys are
    // local ISO dates so they compare cleanly against todayISO.
    sorted
      .filter((o) => !needsResponse(o))
      .forEach((obs) => {
        const key = obs.scheduled_date
          ? toLocalISODate(new Date(obs.scheduled_date))
          : "none";
        const last = sections[sections.length - 1];
        if (last && last.key === key) last.obsList.push(obs);
        else sections.push({ key, obsList: [obs] });
      });
    return {
      listSections: sections.map((s) =>
        s.queue ? s : { key: s.key, items: collapseSlots(s.obsList) },
      ),
      slotTotals: totals,
    };
  }, [sorted]);

  const calendarDays = useMemo(() => calendarDaysFrom(weekAnchor), [weekAnchor]);
  const today = new Date();
  const todayISO = toLocalISODate(today);
  const dayLabel = (key) => {
    const d = parseLocalISODate(key);
    if (!d) return t("upcoming.noDate");
    const dateStr = formatWeekdayMonthDay(d);
    if (key === todayISO) return `${t("schedule.today")} — ${dateStr}`;
    if (key === toLocalISODate(addDays(today, 1)))
      return `${t("upcoming.tomorrow")} — ${dateStr}`;
    return dateStr;
  };
  const rangeStart = calendarDays[0];
  const rangeEnd = calendarDays[calendarDays.length - 1];
  const hasTimetable = timetable.length > 0;

  // Null unless this user may conduct this visit; `{ due }` says whether its
  // day has come. The status pair mirrors what submit_observation accepts, and
  // the ownership test mirrors `can_manage` (observations/scoping.py), which
  // admits the creator and ANY assigned observer. The list payload names only
  // the main observer, so a co-observer is caught by the endpoint's own filter
  // instead: /observations/upcoming/ hands a row only to the observed teacher
  // or to an assigned observer, which makes every OUTGOING row one this user
  // observes.
  const conductState = (obs) => {
    const open =
      obs.status === OBSERVATION_STATUS.SCHEDULED ||
      obs.status === OBSERVATION_STATUS.IN_PROGRESS;
    // The uuid is pulled out first because comparing two `undefined`s is how a
    // row with no main observer would match a caller with no uuid.
    const mineUuid = user?.uuid;
    const mine =
      obs.direction === DIRECTION.OUTGOING ||
      (Boolean(mineUuid) &&
        (obs.main_observer?.uuid === mineUuid || obs.created_by?.uuid === mineUuid));
    if (!open || !mine) return null;
    // A peer visit the colleague has not accepted is not yours to walk into,
    // and the same card says "awaiting a reply" two lines up. Officials carry
    // no confirmation step at all, so they are conductable as soon as they
    // exist. The server would take either — this is the product's rule.
    if (obs.displayStatus === DISPLAY_STATUS.AWAITING) return null;
    return { due: isConductDue(obs.scheduled_date, todayISO) };
  };

  const openConductForm = (obs) => navigate(`/observation?visit=${obs.uuid}`);

  // Visits this user owes whose day has already gone. The endpoint hands them
  // over only because the tab asks for them (`include_overdue`), and the
  // calendar opens on the CURRENT week — so a booking three weeks stale is
  // off-screen in the default view, which is the one place it must not be.
  // Hence a banner rather than trust in the grid.
  const overdueMine = sorted.filter(
    (o) => o.scheduled_date && o.scheduled_date < todayISO && conductState(o),
  );

  const openAddLesson = (weekday, period) => setLessonEdit({ weekday, period });

  // List-card building blocks, shared by the single and grouped variants.
  const dateBadge = (o, outgoing, official) => (
    <div
      className={`w-14 h-14 rounded-2xl flex flex-col items-center justify-center shrink-0 border ${
        outgoing
          ? "bg-indigo-600 text-white border-indigo-500"
          : official
            ? "bg-amber-100 text-amber-700 border-amber-200"
            : "bg-slate-50 text-slate-600 border-slate-200"
      }`}
    >
      <span className="text-[10px] font-black uppercase tracking-tighter">{o.monthShort}</span>
      <span className="text-xl font-black leading-none">{o.dayNum}</span>
    </div>
  );
  const statusPill = (o) => (
    <span
      className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${statusBadgeClass(o.displayStatus)}`}
    >
      {statusLabel(o.displayStatus)}
    </span>
  );
  const typeLabel = (o) =>
    o.type === OBSERVATION_TYPE.OFFICIAL
      ? t("upcoming.officialObservation")
      : t("upcoming.peerVisit");
  const rowActions = (o) => {
    const pending = o.displayStatus === DISPLAY_STATUS.PENDING;
    const declined = o.displayStatus === DISPLAY_STATUS.DECLINED;
    if (o.direction !== DIRECTION.INCOMING || o.type !== OBSERVATION_TYPE.PEER || (!pending && !declined))
      return null;
    return (
      <div className="flex items-center gap-1.5 min-w-0 cursor-default" onClick={(e) => e.stopPropagation()}>
        {pending ? (
          <>
            <button
              type="button"
              onClick={() => onConfirm(o.uuid)}
              className={`h-8 px-2.5 rounded-lg ${ACTION_BTN} ${CONFIRM_BTN} ${FOCUS_RING}`}
            >
              <ActionIcon path={ICON_PATHS.check} className="w-3 h-3 shrink-0" />
              <span className="truncate">{t("upcoming.confirm")}</span>
            </button>
            <button
              type="button"
              onClick={() => onDecline(o.uuid)}
              className={`h-8 px-2.5 rounded-lg ${ACTION_BTN} ${DECLINE_BTN} ${FOCUS_RING}`}
            >
              <ActionIcon path={ICON_PATHS.x} className="w-3 h-3 shrink-0" />
              <span className="truncate">{t("upcoming.decline")}</span>
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => onConfirm(o.uuid)}
            className={`h-8 px-2.5 rounded-lg ${ACTION_BTN} ${RESTORE_BTN} ${FOCUS_RING}`}
          >
            <ActionIcon path={ICON_PATHS.undo} className="w-3 h-3 shrink-0" />
            <span className="truncate">{t("upcoming.restore")}</span>
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-xl font-black text-slate-900 tracking-tight">
            {t("upcoming.scheduled")}
          </h3>
          <p className="text-xs text-slate-500 font-medium">{t("upcoming.scheduledSub")}</p>
        </div>
        <div className="flex items-center gap-2 p-1 bg-slate-200/50 rounded-xl">
          <button
            onClick={() => setCalendarViewMode("list")}
            className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${FOCUS_RING} ${
              calendarViewMode === "list"
                ? "bg-white text-indigo-600 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {t("upcoming.list")}
          </button>
          <button
            onClick={() => setCalendarViewMode("calendar")}
            className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${FOCUS_RING} ${
              calendarViewMode === "calendar"
                ? "bg-white text-indigo-600 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {t("upcoming.calendar")}
          </button>
        </div>
      </div>

      {overdueMine.length > 0 && (
        <div className="flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3">
          <svg className="w-5 h-5 shrink-0 text-rose-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0l-7.1 12.25A2 2 0 004.99 19z" />
          </svg>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-rose-900">
              {t("upcoming.overdueTitle", { count: overdueMine.length })}
            </p>
            <p className="text-xs text-rose-700 font-medium mt-0.5">
              {t("upcoming.overdueBody")}
            </p>
          </div>
          {/* Only offered from the calendar: in list view the oldest booking is
              already the first row, so the button would scroll to what the eye
              has landed on. */}
          {calendarViewMode === "calendar" && (
            <button
              type="button"
              onClick={() => setCalendarViewMode("list")}
              className={`shrink-0 h-8 px-3 rounded-lg bg-rose-600 text-white text-[10px] font-black uppercase tracking-widest hover:bg-rose-700 transition-colors ${FOCUS_RING}`}
            >
              {t("upcoming.overdueShow")}
            </button>
          )}
        </div>
      )}

      {calendarViewMode === "list" ? (
        <div className="space-y-6">
          {sorted.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
              <p className="text-sm text-slate-400">{t("upcoming.none")}</p>
              <button
                type="button"
                onClick={() => onRequestVisit({})}
                className={`mt-5 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 shadow-lg shadow-indigo-100 ${FOCUS_RING}`}
              >
                {t("upcoming.requestPeerVisit")}
              </button>
            </div>
          ) : (
            listSections.map((section) => (
              <section key={section.key} className="space-y-3">
                {section.queue ? (
                  <div className="flex items-center gap-2 px-2">
                    <span className="w-2 h-2 rounded-full bg-amber-400" aria-hidden="true" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                      {t("upcoming.needsResponse")}
                    </span>
                    <span className="px-1.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-black">
                      {section.count}
                    </span>
                    <span className="flex-1 h-px bg-slate-200" />
                  </div>
                ) : (
                  <div className="sticky top-0 z-10 flex items-center gap-3 px-2 py-1.5 bg-slate-50/90 backdrop-blur-sm rounded-lg">
                    <span
                      className={`text-[10px] font-black uppercase tracking-widest ${
                        section.key === todayISO ? "text-indigo-600" : "text-slate-500"
                      }`}
                    >
                      {dayLabel(section.key)}
                    </span>
                    <span className="flex-1 h-px bg-slate-200" />
                  </div>
                )}
                <div className="grid grid-cols-1 gap-4">
                  {section.items.map((list) => {
              // Several visitors at one lesson → one grouped card. Its accent
              // bar follows the most attention-worthy visit inside: pending >
              // official > the rest (same table as single cards).
              if (list.length > 1) {
                const first = list[0];
                const hasOfficial = list.some((o) => o.type === OBSERVATION_TYPE.OFFICIAL);
                const groupRep =
                  list.find((o) => o.displayStatus === DISPLAY_STATUS.PENDING) ||
                  list.find((o) => o.type === OBSERVATION_TYPE.OFFICIAL) ||
                  list.find((o) => o.displayStatus === DISPLAY_STATUS.CONFIRMED) ||
                  first;
                const slotHidden =
                  (slotTotals.get(`${first.scheduled_date}|${first.period}`) || list.length) -
                  list.length;
                return (
                  <div
                    key={`${first.scheduled_date}-${first.period}`}
                    className={`border border-slate-200 border-l-4 rounded-2xl p-5 ${listCardAccent(groupRep).card}`}
                  >
                    <div className="flex items-center gap-4 mb-3">
                      {dateBadge(first, false, hasOfficial)}
                      <div className="min-w-0">
                        <h4 className="text-base font-bold text-slate-900">
                          {t("upcoming.visitorsAtOnce", { count: list.length })}
                        </h4>
                        <p className="text-xs text-slate-500 font-medium mt-0.5">{periodText(first)}</p>
                        {slotHidden > 0 && (
                          <p className="text-[11px] font-bold text-amber-600 mt-0.5">
                            {t("upcoming.moreAtLesson", { count: slotHidden })}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      {list.map((o) => {
                        const actions = rowActions(o);
                        return (
                          <div
                            key={o.uuid}
                            role="button"
                            tabIndex={0}
                            aria-label={`${o.counterpartyName} — ${typeLabel(o)}, ${statusLabel(o.displayStatus)}`}
                            onClick={() => onSelectObservation(o)}
                            onKeyDown={(e) => {
                              if (e.target !== e.currentTarget) return;
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onSelectObservation(o);
                              }
                            }}
                            className={`flex flex-wrap items-center justify-end gap-3 p-3 rounded-xl border border-slate-100 hover:border-indigo-200 hover:bg-indigo-50/40 transition-colors cursor-pointer ${FOCUS_RING}`}
                          >
                            <div className="flex-1 min-w-[8rem]">
                              <span className="text-sm font-bold text-slate-800 block truncate">
                                {o.counterpartyName}
                              </span>
                              <span className="mt-1 inline-flex px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-black uppercase tracking-widest">
                                {typeLabel(o)}
                              </span>
                            </div>
                            {!(actions && o.displayStatus === DISPLAY_STATUS.PENDING) && statusPill(o)}
                            {actions}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }

              const obs = list[0];
              const isOutgoing = obs.direction === DIRECTION.OUTGOING;
              const isOfficial = obs.type === OBSERVATION_TYPE.OFFICIAL;
              const slotKey =
                obs.direction === DIRECTION.INCOMING && obs.scheduled_date && obs.period != null
                  ? `${obs.scheduled_date}|${obs.period}`
                  : null;
              const slotHidden = slotKey ? (slotTotals.get(slotKey) || 1) - 1 : 0;
              const hasInlineActions =
                !isOutgoing &&
                obs.type === OBSERVATION_TYPE.PEER &&
                (obs.displayStatus === DISPLAY_STATUS.PENDING ||
                  obs.displayStatus === DISPLAY_STATUS.DECLINED);
              // Never both: responding is what the observed teacher owes on an
              // incoming visit, conducting is what the observer owes on one
              // they were assigned.
              const conduct = conductState(obs);
              const directionLine = isOutgoing
                ? t("upcoming.youVisitTheirLesson")
                : isOfficial
                  ? t("upcoming.officialOfYourLesson")
                  : t("upcoming.visitsYourLesson");
              const accent = listCardAccent(obs);

              return (
                <div
                  key={obs.uuid}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelectObservation(obs)}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelectObservation(obs);
                    }
                  }}
                  className={`group border border-slate-200 border-l-4 rounded-2xl p-5 hover:shadow-xl transition-all cursor-pointer ${FOCUS_RING} ${accent.card}`}
                >
                  <div className="flex items-start gap-4">
                    {dateBadge(obs, isOutgoing, isOfficial)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4
                          className={`text-base font-bold truncate ${
                            obs.displayStatus === DISPLAY_STATUS.DECLINED ||
                            obs.displayStatus === DISPLAY_STATUS.CANCELLED
                              ? "text-slate-500 line-through"
                              : "text-slate-900"
                          }`}
                        >
                          {isOutgoing
                            ? t("upcoming.visiting", { name: obs.counterpartyName })
                            : obs.counterpartyName}
                        </h4>
                        <span className="shrink-0 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-black uppercase tracking-widest">
                          {typeLabel(obs)}
                        </span>
                      </div>
                      <p className={`mt-0.5 text-[10px] font-black uppercase tracking-widest ${accent.label}`}>
                        {directionLine}
                      </p>
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="flex items-center gap-1.5 text-xs font-bold text-slate-600">
                          <svg
                            className="w-3.5 h-3.5 text-slate-400 shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2.5"
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          {periodText(obs)}
                        </span>
                        {statusPill(obs)}
                      </div>
                      <FocusStandardChips
                        standards={visitFocusStandards(
                          rubric,
                          obs.focus_standards,
                          obs.domain_subject?.uuid,
                        )}
                        className="mt-2.5"
                      />
                      {slotHidden > 0 && (
                        <p className="text-[11px] font-bold text-amber-600 mt-1.5">
                          {t("upcoming.moreAtLesson", { count: slotHidden })}
                        </p>
                      )}
                    </div>
                    <svg
                      className="w-5 h-5 self-center text-slate-300 group-hover:text-indigo-500 transition-colors shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                  {(hasInlineActions || conduct) && (
                    <div
                      className="mt-4 pt-4 border-t border-slate-100 flex gap-2 cursor-default"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {conduct ? (
                        <button
                          type="button"
                          onClick={() => openConductForm(obs)}
                          className={`flex-1 h-10 px-3 rounded-xl ${ACTION_BTN} ${
                            conduct.due ? CONDUCT_BTN : CONDUCT_SOON_BTN
                          } ${FOCUS_RING}`}
                        >
                          <ActionIcon path={ICON_PATHS.conduct} />
                          <span className="truncate">{t("upcoming.conduct")}</span>
                        </button>
                      ) : obs.displayStatus === DISPLAY_STATUS.PENDING ? (
                        <>
                          <button
                            type="button"
                            onClick={() => onConfirm(obs.uuid)}
                            className={`flex-1 h-10 px-3 rounded-xl ${ACTION_BTN} ${CONFIRM_BTN} ${FOCUS_RING}`}
                          >
                            <ActionIcon path={ICON_PATHS.check} />
                            <span className="truncate">{t("upcoming.confirm")}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => onDecline(obs.uuid)}
                            className={`flex-1 h-10 px-3 rounded-xl ${ACTION_BTN} ${DECLINE_BTN} ${FOCUS_RING}`}
                          >
                            <ActionIcon path={ICON_PATHS.x} />
                            <span className="truncate">{t("upcoming.decline")}</span>
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onConfirm(obs.uuid)}
                          className={`flex-1 h-10 px-3 rounded-xl ${ACTION_BTN} ${RESTORE_BTN} ${FOCUS_RING}`}
                        >
                          <ActionIcon path={ICON_PATHS.undo} />
                          <span className="truncate">{t("upcoming.restore")}</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
                  })}
                </div>
              </section>
            ))
          )}
        </div>
      ) : (
        <div className="bg-slate-100/50 p-4 rounded-[32px]">
          {/* CAL-1: week navigation */}
          <div className="flex items-center justify-between flex-wrap gap-3 px-2 pb-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setWeekAnchor((a) => addDays(a, -28))}
                className={`px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-500 hover:text-indigo-600 hover:border-indigo-200 hover:bg-slate-50 text-[10px] font-black uppercase tracking-widest transition-colors ${FOCUS_RING}`}
              >
                {t("schedule.prevWeek")}
              </button>
              <button
                onClick={() => setWeekAnchor(mondayOf(new Date()))}
                className={`px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors ${FOCUS_RING}`}
              >
                {t("schedule.today")}
              </button>
              <button
                onClick={() => setWeekAnchor((a) => addDays(a, 28))}
                className={`px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-500 hover:text-indigo-600 hover:border-indigo-200 hover:bg-slate-50 text-[10px] font-black uppercase tracking-widest transition-colors ${FOCUS_RING}`}
              >
                {t("schedule.nextWeek")}
              </button>
            </div>
            <div className="flex items-center gap-3">
              {rangeStart && rangeEnd && (
                <span className="text-[11px] font-black text-slate-500">
                  {formatShortDate(toLocalISODate(rangeStart))} — {formatShortDate(toLocalISODate(rangeEnd))}
                </span>
              )}
              <input
                type="date"
                aria-label={t("schedule.jumpToDate")}
                title={t("schedule.jumpToDate")}
                onChange={(e) => {
                  const d = parseLocalISODate(e.target.value);
                  if (d) setWeekAnchor(mondayOf(d));
                }}
                className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-[11px] font-bold text-slate-600 outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
              />
            </div>
          </div>

          {/* EDIT-4: empty-state nudge when no lessons published */}
          {!hasTimetable && (
            <div className="mx-2 mb-3 bg-white border border-dashed border-indigo-200 rounded-2xl p-6 text-center">
              <h4 className="text-base font-black text-slate-900">{t("schedule.emptyTitle")}</h4>
              <p className="text-xs text-slate-500 font-medium mt-1 mb-4">{t("schedule.emptySub")}</p>
              <button
                onClick={() => openAddLesson(1, 1)}
                className={`px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 shadow-lg shadow-indigo-100 ${FOCUS_RING}`}
              >
                {t("schedule.addFirstLesson")}
              </button>
            </div>
          )}

          {/* CAL-3: legend */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 pb-3">
            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
              {t("schedule.legendTitle")}:
            </span>
            {LEGEND.map((l) => (
              <span key={l.key} className="flex items-center gap-1.5">
                <span className={`w-3 h-3 rounded-sm ${l.dot}`} />
                <span className="text-[10px] font-bold text-slate-500">{t(`schedule.${l.key}`)}</span>
              </span>
            ))}
          </div>

          <div className="overflow-x-auto">
            <div className="min-w-[640px] flex flex-col gap-4 p-4 pt-0">
            {[0, 1, 2, 3].map((weekIdx) => (
              <div
                key={weekIdx}
                className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm"
              >
                {weekIdx === 0 && (
                  <div className="grid grid-cols-5 border-b border-slate-100 bg-slate-50/50">
                    {["mon", "tue", "wed", "thu", "fri"].map((day) => (
                      <div
                        key={day}
                        className="py-3 text-center border-r border-slate-100 last:border-r-0"
                      >
                        <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                          {t(`upcoming.${day}`)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-5">
                  {calendarDays
                    .slice(weekIdx * 5, (weekIdx + 1) * 5)
                    .map((date, idx) => {
                      const dayOfWeek = date.getDay();
                      const lessons = lessonByDay[dayOfWeek] || EMPTY_DAY;
                      // Declined stays visible (rose, with an undo) until the next
                      // refetch so the teacher can reverse the choice; only
                      // requester-withdrawn visits disappear immediately.
                      const actualObs = sorted.filter(
                        (obs) =>
                          sameLocalDay(date, obs.scheduled_date) &&
                          obs.displayStatus !== DISPLAY_STATUS.CANCELLED,
                      );
                      // CAL-2: keep ALL obs per period (don't silently drop).
                      const periodToObs = new Map();
                      actualObs.forEach((obs) => {
                        if (obs.period != null) {
                          const list = periodToObs.get(obs.period) || [];
                          list.push(obs);
                          periodToObs.set(obs.period, list);
                        }
                      });
                      const isToday =
                        date.getFullYear() === today.getFullYear() &&
                        date.getMonth() === today.getMonth() &&
                        date.getDate() === today.getDate();

                      return (
                        <div
                          key={idx}
                          className={`p-3 border-r border-slate-100 last:border-r-0 ${
                            isToday ? "bg-indigo-50 ring-1 ring-inset ring-indigo-200" : "hover:bg-slate-50/30"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <span
                              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black ${
                                isToday ? "bg-indigo-600 text-white shadow-md" : "text-slate-400"
                              }`}
                            >
                              {date.getDate()}
                            </span>
                            {(idx === 0 || date.getDate() === 1) && (
                              <span className="text-[10px] font-black text-slate-500 uppercase tracking-tighter">
                                {formatMonthShort(date)}
                              </span>
                            )}
                          </div>

                          <div className="space-y-1">
                            {[1, 2, 3, 4, 5, 6, 7, 8].map((p) => {
                              const obsList = periodToObs.get(p);
                              const obs = obsList ? obsList[0] : null;
                              const extra = obsList ? obsList.length - 1 : 0;
                              const lesson = lessons[p];
                              const grade = lesson?.group?.name || null;
                              const time = formatPeriodTime(lesson?.start_time);

                              if (obs) {
                                const isOutgoing = obs.direction === DIRECTION.OUTGOING;
                                // Show the VISITED class (obs.group), not the
                                // viewer's own timetable lesson at this slot.
                                // For incoming visits the two coincide, so fall
                                // back to the own-lesson grade when the
                                // observation never captured one; outgoing
                                // visits get no own-timetable fallback (it would
                                // be the viewer's unrelated class).
                                const visitGrade =
                                  obs.group?.name || (isOutgoing ? null : grade);
                                const confirmed = obs.displayStatus === DISPLAY_STATUS.CONFIRMED;
                                const isDeclined = obs.displayStatus === DISPLAY_STATUS.DECLINED;
                                // Clock = a person still has to act (you, or the
                                // peer you asked). SCHEDULED officials await
                                // nobody — they get no status icon at all.
                                const isWaiting =
                                  obs.displayStatus === DISPLAY_STATUS.PENDING ||
                                  obs.displayStatus === DISPLAY_STATUS.AWAITING;
                                const isIncomingPeer =
                                  obs.direction === DIRECTION.INCOMING &&
                                  obs.type === OBSERVATION_TYPE.PEER;
                                // Whole slot, not just obs[0] — a pending visit
                                // hidden behind "+N" must still surface the bar.
                                const slotHasPending = obsList.some(
                                  (o) =>
                                    o.direction === DIRECTION.INCOMING &&
                                    o.type === OBSERVATION_TYPE.PEER &&
                                    o.displayStatus === DISPLAY_STATUS.PENDING,
                                );
                                // Searched across the whole slot, not just the
                                // visit the cell shows: with two visits at one
                                // lesson, the conductable one need not be
                                // first, and the chooser behind "+N" has no
                                // conduct action of its own to fall back on.
                                const conductable = obsList
                                  .map((o) => ({ obs: o, state: conductState(o) }))
                                  .find((x) => x.state);
                                const openObs = () =>
                                  obsList.length > 1 ? setSlotVisits(obsList) : onSelectObservation(obs);
                                return (
                                  <div
                                    key={obs.uuid}
                                    role="button"
                                    tabIndex={0}
                                    onClick={openObs}
                                    onKeyDown={(e) => {
                                      if (e.target !== e.currentTarget) return;
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        openObs();
                                      }
                                    }}
                                    aria-label={`P${p} ${obs.counterpartyName} ${statusLabel(obs.displayStatus)}`}
                                    className={`group w-full p-2 rounded-xl border text-left transition-all cursor-pointer shadow-sm min-h-[52px] flex flex-col justify-center ${FOCUS_RING} ${calendarCellClass(obs)}`}
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-0.5">
                                          <span
                                            className={`text-[10px] font-black uppercase tracking-widest ${
                                              isOutgoing ? "text-white/90" : "text-slate-500"
                                            }`}
                                          >
                                            P{p}{time ? ` • ${time}` : ""}
                                          </span>
                                          <span
                                            title={statusLabel(obs.displayStatus)}
                                            className="flex items-center gap-1"
                                          >
                                            {confirmed ? (
                                              <svg
                                                className={`w-3.5 h-3.5 ${isOutgoing ? "text-white" : "text-emerald-600"}`}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth="2.5"
                                                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                              </svg>
                                            ) : isDeclined ? (
                                              <svg
                                                className="w-3.5 h-3.5 text-rose-500"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth="2.5"
                                                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                              </svg>
                                            ) : isWaiting ? (
                                              <svg
                                                className={`w-3.5 h-3.5 ${isOutgoing ? "text-white" : "text-amber-500"}`}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth="2.5"
                                                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                              </svg>
                                            ) : null}
                                          </span>
                                        </div>
                                        <p
                                          className={`text-[10px] font-black leading-tight truncate ${isDeclined ? "line-through" : ""}`}
                                        >
                                          {isOutgoing
                                            ? t("upcoming.visitName", { name: obs.counterpartyName })
                                            : obs.counterpartyName}
                                        </p>
                                      </div>
                                      {visitGrade && (
                                        <div
                                          className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-black ${
                                            isOutgoing ? "bg-white/20 text-white" : "bg-slate-100 text-slate-900"
                                          }`}
                                        >
                                          {visitGrade}
                                        </div>
                                      )}
                                    </div>
                                    {conductable && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openConductForm(conductable.obs);
                                        }}
                                        className={`mt-1.5 w-full h-6 px-2 rounded-lg ${ACTION_BTN} ${FOCUS_RING} ${conductCellClass(
                                          conductable.obs,
                                          conductable.state.due,
                                        )}`}
                                      >
                                        <ActionIcon path={ICON_PATHS.conduct} className="w-3 h-3 shrink-0" />
                                        <span className="truncate">{t("upcoming.conduct")}</span>
                                      </button>
                                    )}
                                    {slotHasPending && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openObs();
                                        }}
                                        className={`mt-1.5 w-full h-6 px-2 rounded-lg bg-amber-100 text-amber-800 hover:bg-amber-200 ${ACTION_BTN} ${FOCUS_RING}`}
                                      >
                                        <span className="truncate">{t("upcoming.respond")}</span>
                                      </button>
                                    )}
                                    {isIncomingPeer && isDeclined && (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onConfirm(obs.uuid);
                                        }}
                                        className={`mt-1.5 w-full h-6 px-2 rounded-lg ${ACTION_BTN} ${RESTORE_BTN} ${FOCUS_RING}`}
                                      >
                                        <ActionIcon path={ICON_PATHS.undo} className="w-3 h-3 shrink-0" />
                                        <span className="truncate">{t("upcoming.restore")}</span>
                                      </button>
                                    )}
                                    {extra > 0 && (
                                      <span
                                        className={`mt-1 inline-block text-[10px] font-black uppercase tracking-widest ${
                                          isOutgoing ? "text-white/90" : "text-slate-500"
                                        }`}
                                      >
                                        {t("schedule.plusMore", { count: extra })}
                                      </span>
                                    )}
                                  </div>
                                );
                              }

                              if (grade) {
                                return (
                                  <button
                                    key={`lesson-${p}`}
                                    type="button"
                                    onClick={() => setLessonEdit({ entry: lesson, weekday: dayOfWeek, period: p })}
                                    aria-label={`${t("schedule.editLesson")} P${p} ${grade}`}
                                    className={`w-full group/le p-2 rounded-xl border border-slate-200 bg-slate-50 min-h-[52px] flex flex-col justify-center shadow-sm text-left hover:border-indigo-200 hover:bg-indigo-50/40 transition-all ${FOCUS_RING}`}
                                  >
                                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                                      P{p}{time ? ` • ${time}` : ""}
                                    </span>
                                    <p className="text-xs font-black text-slate-700 leading-tight truncate">
                                      {grade}
                                    </p>
                                  </button>
                                );
                              }

                              return (
                                <button
                                  key={`empty-${p}`}
                                  type="button"
                                  onClick={() =>
                                    setSlotChooser({
                                      date: toLocalISODate(date),
                                      weekday: dayOfWeek,
                                      period: p,
                                    })
                                  }
                                  aria-label={`P${p} ${t("schedule.addLesson")}`}
                                  className={`group w-full h-[52px] border border-dashed border-slate-200 rounded-xl hover:border-indigo-200 hover:bg-indigo-50/30 transition-all cursor-pointer flex items-center justify-center ${FOCUS_RING}`}
                                >
                                  <svg
                                    className="w-4 h-4 text-slate-300 group-hover:text-indigo-400 transition-colors"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2.5"
                                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                    />
                                  </svg>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            ))}
            </div>
          </div>
        </div>
      )}

      {/* Empty-slot chooser: add my lesson vs request a peer visit */}
      {slotChooser && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          onClick={() => setSlotChooser(null)}
        >
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="slot-chooser-title"
            className="relative bg-white rounded-[32px] shadow-2xl w-full max-w-sm p-6 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h4
              id="slot-chooser-title"
              className="text-[10px] font-black uppercase tracking-widest text-slate-500 px-1"
            >
              {t("schedule.slotChooserTitle")}
            </h4>
            <button
              onClick={() => {
                openAddLesson(slotChooser.weekday, slotChooser.period);
                setSlotChooser(null);
              }}
              className={`w-full p-4 rounded-2xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 text-left font-black text-sm text-slate-800 transition-colors ${FOCUS_RING}`}
            >
              {t("schedule.addLesson")}
            </button>
            <button
              onClick={() => {
                onRequestVisit({ date: slotChooser.date, period: slotChooser.period });
                setSlotChooser(null);
              }}
              className={`w-full p-4 rounded-2xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 text-left font-black text-sm text-slate-800 transition-colors ${FOCUS_RING}`}
            >
              {t("upcoming.requestPeerVisit")}
            </button>
          </div>
        </div>
      )}

      {/* Multi-visit slot: pick which visit to open */}
      {slotVisits && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          onClick={() => setSlotVisits(null)}
        >
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="slot-visits-title"
            className="relative bg-white rounded-[32px] shadow-2xl w-full max-w-sm p-6 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h4
              id="slot-visits-title"
              className="text-[10px] font-black uppercase tracking-widest text-slate-500 px-1"
            >
              {t("schedule.slotVisits")} — {t("upcoming.period", { period: slotVisits[0]?.period })}
            </h4>
            {slotVisits.map((obs) => (
              <button
                key={obs.uuid}
                onClick={() => {
                  setSlotVisits(null);
                  onSelectObservation(obs);
                }}
                className={`w-full p-4 rounded-2xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 text-left transition-colors ${FOCUS_RING}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-black text-sm text-slate-800 truncate">
                      {obs.direction === DIRECTION.OUTGOING
                        ? t("upcoming.visiting", { name: obs.counterpartyName })
                        : obs.counterpartyName}
                    </p>
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mt-0.5">
                      {obs.type === OBSERVATION_TYPE.OFFICIAL
                        ? t("upcoming.officialObservation")
                        : t("upcoming.peerVisit")}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${statusBadgeClass(obs.displayStatus)}`}
                  >
                    {statusLabel(obs.displayStatus)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {lessonEdit && (
        <LessonEditorModal
          entry={lessonEdit.entry}
          weekday={lessonEdit.weekday}
          period={lessonEdit.period}
          groups={groups}
          onClose={() => setLessonEdit(null)}
          onSaved={() => onTimetableChanged && onTimetableChanged()}
        />
      )}
    </div>
  );
}
